import { Slot, Slottable } from '@radix-ui/react-slot';
import { cva, type VariantProps } from 'class-variance-authority';
import { Loader2, LucideIcon } from 'lucide-react';
import * as React from 'react';
import { IconType } from 'react-icons/lib';

import { cn } from '@/lib/utils';

const buttonVariants = cva(
  'inline-flex items-center justify-center gap-2 font-semibold whitespace-nowrap rounded-md text-sm ring-offset-background transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring disabled:pointer-events-none disabled:opacity-50 [&_svg]:pointer-events-none [&_svg]:size-4 [&_svg]:shrink-0',
  {
    variants: {
      variant: {
        default:
          'bg-primary text-primary-foreground hover:bg-primary/90 dark:bg-primary-700 dark:text-primary-50 dark:hover:bg-primary-600',
        destructive:
          'bg-destructive text-destructive-foreground hover:bg-destructive/90',
        outline:
          'border border-input bg-background hover:bg-muted/75 hover:text-muted-foreground dark:border-input-700 dark:bg-background-800 dark:text-background-50 dark:hover:bg-accent-700 dark:hover:text-accent-50',
        'primary-outline':
          'border border-primary text-primary bg-background hover:bg-primary hover:text-primary-foreground dark:border-primary-400 dark:text-primary-300 dark:bg-background-800 dark:hover:bg-primary-700 dark:hover:text-primary-50',
        secondary:
          'bg-secondary text-secondary-foreground hover:bg-secondary/80 dark:bg-secondary-700 dark:text-secondary-50 dark:hover:bg-secondary-600',
        accent:
          'bg-accent text-accent-foreground hover:bg-accent/80 dark:bg-accent-700 dark:text-accent-50 dark:hover:bg-accent-600',
        ghost:
          'hover:bg-muted hover:text-muted-foreground dark:text-background-200 dark:hover:bg-muted-700 dark:hover:text-muted-50',
        link: 'text-primary underline-offset-4 hover:underline dark:text-primary-300 dark:hover:text-primary-200',
        success:
          'border-transparent bg-green-100 text-green-800 hover:bg-green-200 dark:bg-green-800 dark:text-green-100 dark:hover:bg-green-700',
        info: 'border-transparent bg-blue-100 text-blue-800 hover:bg-blue-200 dark:bg-blue-800 dark:text-blue-100 dark:hover:bg-blue-700',
        warning:
          'border-transparent bg-amber-100 text-amber-800 hover:bg-amber-200 dark:bg-amber-800 dark:text-amber-100 dark:hover:bg-amber-700',
        'destructive-muted':
          'border-transparent bg-red-100 text-red-800 hover:bg-red-200 dark:bg-red-800 dark:text-red-100 dark:hover:bg-red-700',
        icon: 'px-0 py-0 text-foreground/50 hover:text-foreground',
      },
      effect: {
        none: '',
        expandIcon: 'group gap-0 relative',
        ringHover:
          'transition-all duration-300 hover:ring-2 hover:ring-primary/90 hover:ring-offset-2',
        shine:
          'before:animate-shine relative overflow-hidden before:absolute before:inset-0 before:rounded-[inherit] before:bg-[linear-gradient(45deg,transparent_25%,rgba(255,255,255,0.5)_50%,transparent_75%,transparent_100%)] before:bg-[length:250%_250%,100%_100%] before:bg-no-repeat background-position_0s_ease',
        shineHover:
          'relative overflow-hidden before:absolute before:inset-0 before:rounded-[inherit] before:bg-[linear-gradient(45deg,transparent_25%,rgba(255,255,255,0.5)_50%,transparent_75%,transparent_100%)] before:bg-[length:250%_250%,100%_100%] before:bg-[position:200%_0,0_0] before:bg-no-repeat before:transition-[background-position_0s_ease] hover:before:bg-[position:-100%_0,0_0] before:duration-1000',
        gooeyRight:
          'relative z-0 overflow-hidden transition-all duration-500 before:absolute before:inset-0 before:-z-10 before:translate-x-[150%] before:translate-y-[150%] before:scale-[2.5] before:rounded-[100%] before:bg-gradient-to-r from-neutral-300/40 before:transition-transform before:duration-1000 hover:before:translate-x-[0%] hover:before:translate-y-[0%]',
        gooeyLeft:
          'relative z-0 overflow-hidden transition-all duration-500 after:absolute after:inset-0 after:-z-10 after:translate-x-[-150%] after:translate-y-[150%] after:scale-[2.5] after:rounded-[100%] after:bg-gradient-to-l from-neutral-300/40 after:transition-transform after:duration-1000 hover:after:translate-x-[0%] hover:after:translate-y-[0%]',
        underline:
          'relative !no-underline after:absolute after:bg-primary after:bottom-2 after:h-[1px] after:w-2/3 after:origin-bottom-left after:scale-x-100 hover:after:origin-bottom-right hover:after:scale-x-0 after:transition-transform after:ease-in-out after:duration-300',
        hoverUnderline:
          'relative !no-underline after:absolute after:bg-primary after:bottom-2 after:h-[1px] after:w-2/3 after:origin-bottom-right after:scale-x-0 hover:after:origin-bottom-left hover:after:scale-x-100 after:transition-transform after:ease-in-out after:duration-300',
      },
      size: {
        default: 'h-10 px-4 py-2',
        sm: 'h-9 rounded-md px-3',
        lg: 'h-11 rounded-md px-8',
        icon: 'size-10',
      },
    },
    defaultVariants: {
      variant: 'default',
      size: 'default',
      effect: 'none',
    },
  },
);

export interface ButtonProps
  extends React.ButtonHTMLAttributes<HTMLButtonElement>,
    VariantProps<typeof buttonVariants> {
  asChild?: boolean;
  isLoading?: boolean;
}

type PropsWithIconRight = ButtonProps & {
  iconRight?: IconType | LucideIcon;
  iconLeft?: undefined;
};

type PropsWithIconLeft = ButtonProps & {
  iconRight?: undefined;
  iconLeft?: IconType | LucideIcon;
};

type Props = PropsWithIconRight | PropsWithIconLeft;

const Button = React.forwardRef<HTMLButtonElement, Props>(
  (
    {
      className,
      variant,
      size,
      asChild = false,
      isLoading,
      effect,
      iconRight: IconRight,
      iconLeft: IconLeft,
      disabled,
      ...props
    },
    ref,
  ) => {
    const Comp = asChild ? Slot : 'button';
    const isSizeIcon = size === 'icon';
    const Left =
      !isSizeIcon && IconLeft ? (
        isLoading ? (
          <Loader2 className='mr-2 animate-spin' />
        ) : (
          <IconLeft />
        )
      ) : null;

    const Right =
      !isSizeIcon && !IconLeft && isLoading ? (
        <Loader2 className='ml-2 animate-spin' />
      ) : IconRight ? (
        <IconRight />
      ) : null;

    return (
      <Comp
        className={cn(
          'flex items-center gap-2',
          buttonVariants({ variant, size, effect, className }),
        )}
        disabled={isLoading || disabled}
        ref={ref}
        {...props}
      >
        {Left}
        <Slottable>
          {isSizeIcon && isLoading ? (
            <Loader2 className='animate-spin' />
          ) : (
            props.children
          )}
        </Slottable>
        {Right}
      </Comp>
    );
  },
);
Button.displayName = 'Button';

export { Button, buttonVariants };
